import type { LabeledMapSpace } from "../types";
import { formatAddressShort } from "../utils/address";

/** Info balloon anchored above a marker: solo space name/address, or cluster
 * members grouped by short address. */
export function InfoBalloon<S extends LabeledMapSpace>({
  isCluster,
  group,
  representative,
  getDisplayName,
}: {
  isCluster: boolean;
  group: S[];
  representative: S;
  getDisplayName?: (space: S) => string;
}) {
  const displayName = getDisplayName
    ? getDisplayName(representative)
    : representative.roomName;

  if (!isCluster) {
    return (
      <div className="absolute bottom-full left-10 ml-2 mb-2 bg-white px-3 py-2 rounded-lg shadow-md whitespace-nowrap animate-[fadeInScale_0.3s_ease-out]">
        <div className="font-semibold text-sm text-black mb-0.5">
          {displayName}
        </div>
        {representative.address && (
          <div className="text-xs text-gray-600">
            {formatAddressShort(representative.address)}
          </div>
        )}
      </div>
    );
  }

  const byAddress = group.reduce<Map<string, S[]>>((acc, s) => {
    const key = formatAddressShort(s.address) || s.id;
    if (!acc.has(key)) acc.set(key, []);
    acc.get(key)!.push(s);
    return acc;
  }, new Map());

  return (
    <div className="absolute bottom-full left-10 ml-2 mb-2 bg-white px-3 py-2.5 rounded-lg shadow-md animate-[fadeInScale_0.3s_ease-out] min-w-[160px] max-w-[280px]">
      {Array.from(byAddress.entries()).map(([addr, rooms]) => (
        <div key={addr} className={byAddress.size > 1 ? "mb-2 last:mb-0" : ""}>
          <div className="font-semibold text-sm text-black mb-1">{addr}</div>
          <ul className="list-none pl-0 space-y-0.5 text-xs text-gray-600">
            {rooms.map((s) => (
              <li key={s.id}>
                {getDisplayName ? getDisplayName(s) : s.roomName || "—"}
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
}
